import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * Beem — Login editorial.
 *
 * Diseño único: no es split panel corporativo. Es UNA sola composición asimétrica
 * con:
 *  - flor floating (extraída del logo) grande, rotada, semi-transparente en el fondo
 *  - "carta" del formulario tipo papel artesanal con líneas ornamentales
 *  - contador ornamental del año en el pie (2026)
 *  - número secuencial (01 / 02) para cada campo, tipo ficha de librería
 *  - typografía serif italic grande para la bienvenida
 */
export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [telefono, setTelefono] = useState("");
  const [roleDeseado, setRoleDeseado] = useState("vendedor");
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    // Validaciones locales antes de golpear el backend
    if (mode === "register") {
      if (!roleDeseado) {
        toast.error("Elige si vas a trabajar como vendedor u operador");
        return;
      }
      if (roleDeseado === "vendedor" && !telefono.trim()) {
        toast.error("El teléfono es obligatorio para vendedores. Aparecerá en los PDF de tus pedidos.");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        const res = await register({
          email, password, name,
          telefono: telefono.trim() || null,
          role_deseado: roleDeseado,
        });
        // Si el backend devuelve pending=true, mostrar pantalla de confirmación
        // en lugar de iniciar sesión.
        if (res?.pending) {
          setPendingMessage(res.message || "Tu solicitud fue enviada. Espera la aprobación de un director.");
          return;
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden bg-cream flex items-center justify-center p-4 md:p-8">
      {/* ---- Fondo: flor grande semi-transparente rotada ---- */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[10%] -bottom-[20%] w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] opacity-[0.14] animate-[float-slow_18s_ease-in-out_infinite]"
        style={{
          backgroundImage: 'url(/beem-logo.jpg)',
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
          transform: 'rotate(-15deg)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[15%] -top-[15%] w-[55vw] h-[55vw] max-w-[700px] max-h-[700px] opacity-[0.08] animate-[float-slow_22s_ease-in-out_infinite_reverse]"
        style={{
          backgroundImage: 'url(/beem-logo.jpg)',
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat',
          transform: 'rotate(28deg)',
        }}
      />
      <style>{`
        @keyframes float-slow {
          0%, 100% { transform: rotate(-15deg) translateY(0); }
          50% { transform: rotate(-11deg) translateY(-20px); }
        }
      `}</style>

      {/* ---- Etiquetas ornamentales de esquina ---- */}
      {/* Logo esquina superior — se apila en móvil para no montar sobre el card */}
      <div className="hidden md:flex absolute top-8 left-10 items-center gap-3 z-10">
        <div className="w-10 h-10 rounded-2xl overflow-hidden ring-1 ring-primary/25 shadow-rose-glow">
          <img src="/beem-logo.jpg" alt="Beem" className="w-full h-full object-cover" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-2xl italic text-foreground -mb-0.5">beem</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Importaciones · Colombia</div>
        </div>
      </div>

      <div className="absolute top-8 right-10 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold hidden md:block">
        MMXXVI
      </div>

      <div className="absolute bottom-8 left-10 text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold hidden md:block">
        Sistema operativo interno
      </div>
      <div className="absolute bottom-8 right-10 text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold hidden md:block">
        v3.0
      </div>

      {/* ---- Carta ---- */}
      <div className="relative z-10 w-full max-w-[520px]">
        {/* Logo mobile arriba del card */}
        <div className="md:hidden flex items-center gap-3 justify-center mb-6">
          <div className="w-12 h-12 rounded-2xl overflow-hidden ring-1 ring-primary/25 shadow-rose-glow">
            <img src="/beem-logo.jpg" alt="Beem" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-3xl italic text-foreground -mb-0.5">beem</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Importaciones · Colombia</div>
          </div>
        </div>
        {/* Título editorial */}
        <div className="mb-8 text-center animate-slide-up">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] uppercase tracking-[0.22em] font-semibold mb-4">
            <Sparkles className="w-3 h-3" />
            {mode === "login" ? "Ingreso" : "Nueva cuenta"}
          </div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.05] text-foreground" style={{ fontStyle: "italic" }}>
            {mode === "login" ? (
              <>Continúe<br /><span className="text-primary">construyendo Beem.</span></>
            ) : (
              <>Bienvenida<br /><span className="text-primary">al equipo.</span></>
            )}
          </h1>
          <p className="text-foreground/70 mt-4 max-w-sm mx-auto text-sm">
            {mode === "login"
              ? "Cada pedido, cada cliente, cada contenedor: el motor de su negocio está listo para hoy."
              : "Complete estos datos y comenzamos a trabajar juntos."}
          </p>
        </div>

        {/* Formulario tipo ficha */}
        {pendingMessage ? (
          <div className="relative bg-card/70 backdrop-blur-md rounded-[28px] border border-border shadow-soft-lift p-8 md:p-10 text-center animate-slide-up" data-testid="register-pending-screen">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 px-4 rounded-full bg-emerald-600 text-white text-[10px] uppercase tracking-[0.22em] font-bold flex items-center shadow-soft-lift">
              ✓ Solicitud recibida
            </div>
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 mt-2">
              <Sparkles className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-display italic text-foreground mb-3">
              ¡Casi listo, <span className="text-primary">{name.split(" ")[0]}</span>!
            </h2>
            <p className="text-sm text-foreground/75 leading-relaxed max-w-sm mx-auto">
              {pendingMessage}
            </p>
            <div className="mt-6 p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs text-foreground/70 max-w-sm mx-auto">
              <b>Tu correo:</b> {email}<br />
              <b>Rol solicitado:</b> {roleDeseado === "vendedor" ? "Vendedor" : "Operador"}
            </div>
            <button
              type="button"
              onClick={() => {
                setPendingMessage(null);
                setMode("login");
                setName(""); setPassword(""); setTelefono("");
              }}
              className="mt-6 text-xs text-primary hover:underline font-semibold"
              data-testid="pending-back-to-login"
            >
              ← Volver al inicio de sesión
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="relative bg-card/70 backdrop-blur-md rounded-[28px] border border-border shadow-soft-lift p-8 md:p-10 animate-slide-up" style={{ animationDelay: "0.15s", animationFillMode: "backwards" }}>
          {/* Cinta rose superior */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 px-4 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.22em] font-bold flex items-center shadow-rose-glow">
            ✻ Beem · {new Date().getFullYear()}
          </div>

          <div className="space-y-5 mt-2">
            {mode === "register" && (
              <FieldRow index="01" label="Nombre completo" hint="Como quiere que la vean sus compañeros">
                <Input data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="h-11 border-0 border-b border-border rounded-none px-0 bg-transparent focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-lg placeholder:text-muted-foreground/40" placeholder="Juan Pérez" />
              </FieldRow>
            )}
            {mode === "register" && (
              <FieldRow index="02" label="¿En qué rol vas a trabajar?" hint={roleDeseado === "vendedor" ? "Vendes al público y creas pedidos" : "Recibes pedidos, despachas y manejas inventario"}>
                <div className="flex gap-2 mt-2" data-testid="register-role-selector">
                  <button
                    type="button"
                    onClick={() => setRoleDeseado("vendedor")}
                    className={`flex-1 h-12 rounded-lg border-2 text-sm font-semibold transition-all ${
                      roleDeseado === "vendedor"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-transparent text-muted-foreground hover:border-primary/40"
                    }`}
                    data-testid="register-role-vendedor"
                  >
                    👗 Vendedor
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoleDeseado("operador")}
                    className={`flex-1 h-12 rounded-lg border-2 text-sm font-semibold transition-all ${
                      roleDeseado === "operador"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-transparent text-muted-foreground hover:border-primary/40"
                    }`}
                    data-testid="register-role-operador"
                  >
                    📦 Operador
                  </button>
                </div>
              </FieldRow>
            )}
            {mode === "register" && (
              <FieldRow
                index="03"
                label={roleDeseado === "vendedor" ? "Teléfono / WhatsApp *" : "Teléfono / WhatsApp (opcional)"}
                hint={roleDeseado === "vendedor" ? "Obligatorio — aparecerá en los PDF de tus pedidos como contacto del asesor" : "Solo para que el equipo te ubique"}
              >
                <Input
                  data-testid="register-telefono-input"
                  required={roleDeseado === "vendedor"}
                  type="tel"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className="h-11 border-0 border-b border-border rounded-none px-0 bg-transparent focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-lg placeholder:text-muted-foreground/40"
                  placeholder="+57 300 000 0000"
                />
              </FieldRow>
            )}
            <FieldRow index={mode === "register" ? "04" : "01"} label="Correo electrónico" hint="El mismo que usa en la empresa">
              <Input data-testid="login-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 border-0 border-b border-border rounded-none px-0 bg-transparent focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-lg placeholder:text-muted-foreground/40" placeholder="usted@beem.co" />
            </FieldRow>
            <FieldRow index={mode === "register" ? "05" : "02"} label="Contraseña" hint="Guárdela en un lugar seguro">
              <Input data-testid="login-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 border-0 border-b border-border rounded-none px-0 bg-transparent focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 text-lg placeholder:text-muted-foreground/40" placeholder="••••••••" />
            </FieldRow>
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-14 text-base mt-8 group"
          >
            {loading ? "Un momento..." : (mode === "login" ? "Entrar a Beem" : "Crear mi cuenta")}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </form>
        )}

        {/* Detalle inferior */}
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60 font-semibold">
          <span className="w-6 h-px bg-border" />
          Hecho con corazón para Beem
          <span className="w-6 h-px bg-border" />
        </div>
      </div>
    </div>
  );
}

function FieldRow({ index, label, hint, children }) {
  return (
    <div className="flex gap-4">
      <div className="pt-6 shrink-0">
        <div className="text-[10px] font-mono font-bold text-primary/60 tabular-nums">{index}</div>
      </div>
      <div className="flex-1">
        <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{label}</Label>
        {children}
        {hint && <div className="text-[10px] text-muted-foreground/60 mt-1.5">{hint}</div>}
      </div>
    </div>
  );
}
